/**
 * The Output section's body: a "Will create: …" line derived live from the
 * dialog's current settings, then any estimates / warnings, then the
 * result-name field (`children`, labelled "Result name" everywhere).
 *
 * Must stay inside the dialog's `<form>` body (not the shell footer): the
 * name field is a form control.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface OutputSummaryProps {
  /** What the run produces. Falsy entries are skipped, so a caller can write
   *  `exportDxf && t('…dxf')` inline. */
  creates: ReactNode[];
  warnings?: ReactNode[];
  /** One entry per line (a list under "Will create:") instead of one
   *  sentence joined by " + " — for entries that are whole lines already
   *  (Compare surfaces: one per result). */
  stacked?: boolean;
  /** A muted note under the list, said once (e.g. a CRS caveat). */
  footnote?: ReactNode;
  testId?: string;
  children?: ReactNode;
}

const present = (n: ReactNode) => n !== null && n !== undefined && n !== false && n !== '';

export function OutputSummary({
  creates,
  warnings,
  stacked = false,
  footnote,
  testId = 'output-summary',
  children,
}: OutputSummaryProps) {
  const { t } = useTranslation('project');
  const items = creates.filter(present);
  const warns = (warnings ?? []).filter(present);
  // Nothing ticked yet: say so, muted, rather than leave "Will create:" hanging.
  const nothing = (
    <span data-testid={`${testId}-nothing`} className='text-muted-foreground'>
      {t('analysisConfig.willCreateNothing')}
    </span>
  );
  return (
    <div className='flex min-w-0 flex-col gap-3'>
      {stacked ? (
        <div data-testid={`${testId}-creates`} className='text-sm break-words text-slate-700 dark:text-stone-300'>
          <p className='font-medium'>{t('analysisConfig.willCreate')}</p>
          {items.length === 0 ? (
            <p className='mt-1'>{nothing}</p>
          ) : (
            <ul className='mt-1 flex min-w-0 flex-col gap-1'>
              {items.map((item, i) => (
                <li key={i} data-testid={`${testId}-item`}>
                  {item}
                </li>
              ))}
            </ul>
          )}
          {present(footnote) && (
            <p data-testid={`${testId}-footnote`} className='mt-1 text-xs text-muted-foreground'>
              {footnote}
            </p>
          )}
        </div>
      ) : (
        <p data-testid={`${testId}-creates`} className='text-sm break-words text-slate-700 dark:text-stone-300'>
          <span className='font-medium'>{t('analysisConfig.willCreate')}</span>{' '}
          {items.length === 0
            ? nothing
            : items.map((item, i) => (
                <span key={i}>
                  {i > 0 && ' + '}
                  <span data-testid={`${testId}-item`}>{item}</span>
                </span>
              ))}
        </p>
      )}
      {warns.length > 0 && (
        <ul data-testid={`${testId}-warnings`} className='text-xs break-words text-amber-700 dark:text-amber-400'>
          {warns.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}
